import type { BenefitsGateway } from './BenefitsGateway';
import { BENEFIT_CATALOG, ANNUAL_BONUS_NOTE, ANNUAL_BONUS_PLAN_CODE } from './catalogData';
import { BENEFIT_CATEGORY_CODES } from './categories';
import { declaredFromInput, declaredTaxRefusal } from './declaredTaxProfile';
import { endDateRefusal, resolveOverlap } from './electionRules';
import { addDays, todayIso } from './dates';
import { loadEmployeeDirectory } from './employeeDirectory';
import { seedElections, seedPlans } from './mockBenefitsData';
import { findTaxProfile } from './mockReference';
import {
  guardRead, guardWrite, matchesSearch, newId, paginate, problem, recentChanges, type AuditEvent,
} from './mockSupport';
import { TAX_REVIEW_ISSUE, hasPayrollContribution, isApprovedAndEffective, normalizeKey, validateBenefitPlanShape } from './rules';
import {
  BdErrorCode, UNSUPPORTED_CONTRIBUTION_METHODS,
  type BenefitCatalogItem, type BenefitElection, type BenefitOverview, type BenefitPlan, type BenefitPlanInput, type CatalogQuery, type ConfigurationStatus,
  type CreateBenefitElectionInput, type ElectionQuery, type ListQuery, type Paged, type UpdateBenefitPlanInput,
} from './types';

const LEAVE_NOTE = 'Balances and accruals are managed in Time and Leave.';

/** In effect today: it has started and has no end date or an end date still ahead (the server's `active`). */
function inEffect(e: BenefitElection, today: string): boolean {
  return e.effectiveStartDate <= today && (e.effectiveEndDate === null || e.effectiveEndDate > today);
}

/**
 * The Mock Benefits gateway: an in-memory server that follows the SAME rules as docs/benefits-deductions-contract.md section 4.1 (normalized duplicate
 * refusal, field validation, optimistic concurrency, TaxReviewRequired status when no approved tax profile exists, the election guard), so the screens
 * behave the same against the mock as against the real API. The catalog is the real starter catalog; the plans and elections are SYNTHETIC fixtures.
 * Call `resetForTests()` between tests: the gateway is a module singleton.
 */
export class MockBenefitsGateway implements BenefitsGateway {
  private plans: BenefitPlan[] = [];
  private elections: BenefitElection[] = [];
  private hidden = new Set<string>();
  private audit: AuditEvent[] = [];

  constructor() {
    this.resetForTests();
  }

  /** Test helper (and the way a hard reload starts fresh): restores the synthetic fixtures. */
  resetForTests(): void {
    this.hidden = new Set();
    this.audit = [];
    this.plans = seedPlans().map((s) => this.build(s.id, s.input, { concurrencyVersion: 1, createdAtUtc: new Date().toISOString(), updatedAtUtc: null }));
    this.elections = seedElections();
    const now = Date.now();
    this.audit = [
      { eventType: 'BenefitPlanCreated', occurredAtUtc: new Date(now - 3_600_000).toISOString(), summary: 'Plan SYNTHETIC Medical (employee only) was created.' },
      { eventType: 'BenefitPlanUpdated', occurredAtUtc: new Date(now - 7_200_000).toISOString(), summary: 'Plan SYNTHETIC Dental was updated.' },
    ];
  }

  /** The configuration a plan has right now: a payroll contribution needs an approved, effective tax profile; no contribution needs none. */
  private configuration(input: BenefitPlanInput): { status: ConfigurationStatus; issues: string[] } {
    if (!hasPayrollContribution(input.contributionMethod)) return { status: 'Complete', issues: [] };
    const profile = findTaxProfile(input.taxProfileCode);
    if (isApprovedAndEffective(profile, todayIso())) return { status: 'Complete', issues: [] };
    return { status: 'TaxReviewRequired', issues: [TAX_REVIEW_ISSUE] };
  }

  private build(id: string, input: BenefitPlanInput, meta: { concurrencyVersion: number; createdAtUtc: string; updatedAtUtc: string | null }): BenefitPlan {
    const { status, issues } = this.configuration(input);
    const type = input.typeCode ? BENEFIT_CATALOG.find((c) => c.code === input.typeCode) : undefined;
    const leaveLinked = type?.leaveLinked ?? false;
    return {
      ...input,
      coverageTiers: input.coverageTiers.map((t) => ({ ...t })),
      id,
      isCustom: input.typeCode === null,
      configurationStatus: status,
      configurationIssues: issues,
      createsPayrollDeduction: hasPayrollContribution(input.contributionMethod),
      leaveLinked,
      leaveNote: leaveLinked ? LEAVE_NOTE : input.typeCode === ANNUAL_BONUS_PLAN_CODE ? ANNUAL_BONUS_NOTE : null,
      enrolledCount: 0,
      ...meta,
    };
  }

  private withCounts(plan: BenefitPlan): BenefitPlan {
    const today = todayIso();
    const count = this.elections.filter((e) => e.benefitCodeId === plan.id && !e.isWaived && inEffect(e, today)).length;
    return { ...plan, enrolledCount: count };
  }

  private record(eventType: string, summary: string): void {
    this.audit.push({ eventType, occurredAtUtc: new Date().toISOString(), summary });
  }

  // ---- catalog ----

  async listCatalog(query: CatalogQuery = {}): Promise<BenefitCatalogItem[]> {
    guardRead();
    return BENEFIT_CATALOG
      .filter((c) => !query.category || c.categoryCode === query.category)
      .filter((c) => matchesSearch(query.search, c.name, c.description))
      .map((c) => ({ ...c, isHidden: this.hidden.has(c.code) }))
      .filter((c) => query.includeHidden || !c.isHidden)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  async hideCatalogType(code: string): Promise<void> {
    guardWrite();
    const item = BENEFIT_CATALOG.find((c) => c.code === code);
    if (!item) throw problem(404, BdErrorCode.NotFound, 'That benefit type was not found.');
    this.hidden.add(code);
    this.record('BenefitCatalogTypeHidden', `Benefit type ${item.name} was hidden.`);
  }

  async showCatalogType(code: string): Promise<void> {
    guardWrite();
    const item = BENEFIT_CATALOG.find((c) => c.code === code);
    if (!item) throw problem(404, BdErrorCode.NotFound, 'That benefit type was not found.');
    this.hidden.delete(code);
    this.record('BenefitCatalogTypeShown', `Benefit type ${item.name} was shown.`);
  }

  // ---- plans ----

  async listPlans(query: ListQuery = {}): Promise<Paged<BenefitPlan>> {
    guardRead();
    const status = query.status ?? 'all';
    let rows = this.plans.map((p) => this.withCounts(p));
    rows = rows.filter((p) => matchesSearch(query.search, p.name, p.shortCode, p.providerName));
    if (query.category) rows = rows.filter((p) => p.categoryCode === query.category);
    if (status === 'active') rows = rows.filter((p) => p.isActive);
    if (status === 'inactive') rows = rows.filter((p) => !p.isActive);
    if (status === 'incomplete') rows = rows.filter((p) => p.configurationStatus !== 'Complete');
    const on = query.effectiveOn;
    if (on) rows = rows.filter((p) => (!p.effectiveStartDate || p.effectiveStartDate <= on) && (!p.effectiveEndDate || p.effectiveEndDate >= on));

    const statusRank = (p: BenefitPlan) => (!p.isActive ? 2 : p.configurationStatus !== 'Complete' ? 1 : 0);
    const compareName = (a: BenefitPlan, b: BenefitPlan) => a.name.localeCompare(b.name);
    const sort = query.sort ?? 'name';
    rows = [...rows].sort((a, b) => {
      switch (sort) {
        case '-name': return compareName(b, a);
        case 'category': return a.categoryCode.localeCompare(b.categoryCode) || compareName(a, b);
        case 'effectiveStart': return (a.effectiveStartDate ?? '').localeCompare(b.effectiveStartDate ?? '') || compareName(a, b);
        case '-effectiveStart': return (b.effectiveStartDate ?? '').localeCompare(a.effectiveStartDate ?? '') || compareName(a, b);
        case 'status': return statusRank(a) - statusRank(b) || compareName(a, b);
        default: return compareName(a, b);
      }
    });
    return paginate(rows, query.page, query.pageSize);
  }

  private find(id: string): BenefitPlan {
    const plan = this.plans.find((p) => p.id === id);
    if (!plan) throw problem(404, BdErrorCode.NotFound, 'That benefit plan was not found.');
    return plan;
  }

  async getPlan(id: string): Promise<BenefitPlan> {
    guardRead();
    return this.withCounts(this.find(id));
  }

  /** Every check the server makes before it writes a plan. `selfId` is the plan being edited, so it never collides with itself. */
  private validate(input: BenefitPlanInput, selfId: string | null): void {
    if ((UNSUPPORTED_CONTRIBUTION_METHODS as readonly string[]).includes(input.contributionMethod as string)) {
      throw problem(400, BdErrorCode.UnsupportedContributionMethod, 'This contribution method needs review before it can be offered.');
    }
    const issues = validateBenefitPlanShape(input);
    if (issues.length > 0) throw problem(400, BdErrorCode.ValidationFailed, issues[0]!.message, { errors: issues });

    if (input.typeCode !== null) {
      const type = BENEFIT_CATALOG.find((c) => c.code === input.typeCode);
      if (!type || type.categoryCode !== input.categoryCode) {
        throw problem(400, BdErrorCode.ValidationFailed, 'The benefit type must be an active catalog type of the same category.');
      }
    }
    if (input.taxProfileCode) {
      if (!isApprovedAndEffective(findTaxProfile(input.taxProfileCode), todayIso())) {
        throw problem(400, BdErrorCode.TaxProfileNotApproved, 'The tax profile is not approved for payroll. Tax treatment must be approved before this plan can be used in payroll.');
      }
    }
    const name = normalizeKey(input.name);
    const code = normalizeKey(input.shortCode);
    for (const other of this.plans) {
      if (other.id === selfId) continue;
      if (normalizeKey(other.name) === name) throw problem(409, BdErrorCode.DuplicateName, 'A benefit plan with this name already exists.');
      if (normalizeKey(other.shortCode) === code) throw problem(409, BdErrorCode.DuplicateCode, 'A benefit plan with this short code already exists.');
    }
  }

  async createPlan(input: BenefitPlanInput): Promise<BenefitPlan> {
    guardWrite();
    this.validate(input, null);
    const refusal = declaredTaxRefusal(input.declaredTaxProfile);
    if (refusal) throw problem(400, refusal.code, refusal.message);
    const now = new Date().toISOString();
    // A declared profile is recorded for review only: the plan stays awaiting tax review (configurationStatus is decided by an approved tax profile code alone).
    const plan = {
      ...this.build(newId(), { ...input, name: input.name.trim(), shortCode: input.shortCode.trim() }, { concurrencyVersion: 1, createdAtUtc: now, updatedAtUtc: null }),
      declaredTaxProfile: declaredFromInput(input.declaredTaxProfile, now),
    };
    this.plans = [...this.plans, plan];
    this.record('BenefitPlanCreated', `Plan ${plan.name} was created.`);
    return this.withCounts(plan);
  }

  async updatePlan(id: string, input: UpdateBenefitPlanInput): Promise<BenefitPlan> {
    guardWrite();
    const existing = this.find(id);
    if (input.expectedVersion !== existing.concurrencyVersion) {
      throw problem(409, BdErrorCode.VersionConflict, 'This plan was changed by someone else after you opened it. Your changes were not saved.');
    }
    if (input.declaredTaxProfile) throw problem(400, BdErrorCode.DeclaredTaxProfileNotEditable, 'A declared tax profile cannot be changed yet; changing a tax treatment needs a new effective-dated version.');
    const inUse = this.elections.some((e) => e.benefitCodeId === id);
    if (inUse && (input.contributionMethod !== existing.contributionMethod || input.categoryCode !== existing.categoryCode)) {
      throw problem(409, BdErrorCode.PlanHasElections, 'A plan that has elections cannot change its contribution method or category. Create a new plan instead.');
    }
    this.validate(input, id);
    const updated = this.build(id, { ...input, name: input.name.trim(), shortCode: input.shortCode.trim() }, {
      concurrencyVersion: existing.concurrencyVersion + 1, createdAtUtc: existing.createdAtUtc, updatedAtUtc: new Date().toISOString(),
    });
    updated.declaredTaxProfile = existing.declaredTaxProfile ?? null;
    this.plans = this.plans.map((p) => (p.id === id ? updated : p));
    this.record('BenefitPlanUpdated', `Plan ${updated.name} was updated.`);
    return this.withCounts(updated);
  }

  private setActive(id: string, isActive: boolean): BenefitPlan {
    guardWrite();
    const existing = this.find(id);
    const updated: BenefitPlan = { ...existing, isActive, concurrencyVersion: existing.concurrencyVersion + 1, updatedAtUtc: new Date().toISOString() };
    this.plans = this.plans.map((p) => (p.id === id ? updated : p));
    this.record(isActive ? 'BenefitPlanReactivated' : 'BenefitPlanDeactivated', `Plan ${updated.name} was ${isActive ? 'reactivated' : 'deactivated'}.`);
    return this.withCounts(updated);
  }

  async deactivatePlan(id: string): Promise<BenefitPlan> {
    return this.setActive(id, false);
  }

  async reactivatePlan(id: string): Promise<BenefitPlan> {
    return this.setActive(id, true);
  }

  // ---- overview ----

  async getOverview(): Promise<BenefitOverview> {
    guardRead();
    const today = todayIso();
    const horizon = addDays(today, 60);
    const activePlans = this.plans.filter((p) => p.isActive);
    const open = this.elections.filter((e) => inEffect(e, today));
    const enrolled = open.filter((e) => !e.isWaived);
    const planOf = (id: string) => this.plans.find((p) => p.id === id);

    let employeeTotal = 0;
    let employerTotal = 0;
    let percentPlans = 0;
    const percentPlanIds = new Set<string>();
    for (const e of enrolled) {
      const plan = planOf(e.benefitCodeId);
      if (!plan) continue;
      if (plan.contributionMethod === 'FixedPerPayroll') {
        employeeTotal += e.amountOverride ?? plan.employeeContributionAmount ?? 0;
        employerTotal += plan.employerContributionAmount ?? 0;
      } else if (plan.contributionMethod === 'TieredByCoverage') {
        const tier = plan.coverageTiers.find((t) => t.coverageLevel === e.coverageLevel);
        employeeTotal += tier?.employeeAmount ?? 0;
        employerTotal += tier?.employerAmount ?? 0;
      } else if (['PercentOfEligiblePay', 'EmployerPaidPercent', 'EmployeePaidPercent'].includes(plan.contributionMethod)) {
        percentPlanIds.add(plan.id);
      }
    }
    percentPlans = percentPlanIds.size;
    const monthlyPlans = new Set(enrolled.map((e) => planOf(e.benefitCodeId)).filter((p) => p?.contributionMethod === 'FixedPerMonth').map((p) => p!.id)).size;

    const notes = ['Estimates include fixed amounts and coverage-tier amounts only.'];
    if (percentPlans > 0) notes.push(`${percentPlans} ${percentPlans === 1 ? 'plan uses' : 'plans use'} a percentage of pay, which is calculated at payroll and not included.`);
    if (monthlyPlans > 0) notes.push(`${monthlyPlans} ${monthlyPlans === 1 ? 'plan uses' : 'plans use'} a monthly amount, which is not converted to a per-payroll figure here.`);

    const upcoming: BenefitOverview['upcoming'] = [];
    for (const p of activePlans) {
      if (p.effectiveStartDate && p.effectiveStartDate > today && p.effectiveStartDate <= horizon) upcoming.push({ kind: 'PlanStart', date: p.effectiveStartDate, name: p.name });
      if (p.effectiveEndDate && p.effectiveEndDate >= today && p.effectiveEndDate <= horizon) upcoming.push({ kind: 'PlanEnd', date: p.effectiveEndDate, name: p.name });
    }
    for (const e of this.elections) {
      if (e.effectiveStartDate > today && e.effectiveStartDate <= horizon) upcoming.push({ kind: 'ElectionStart', date: e.effectiveStartDate, name: planOf(e.benefitCodeId)?.name ?? 'Plan' });
    }
    upcoming.sort((a, b) => a.date.localeCompare(b.date));

    return {
      asOf: today,
      totalActivePlans: activePlans.length,
      plansAwaitingConfiguration: activePlans.filter((p) => p.configurationStatus !== 'Complete').length,
      plansApproachingExpiration: activePlans
        .filter((p) => p.effectiveEndDate && p.effectiveEndDate >= today && p.effectiveEndDate <= horizon)
        .map((p) => ({ id: p.id, name: p.name, effectiveEndDate: p.effectiveEndDate! })),
      enrolledEmployees: new Set(enrolled.map((e) => e.employeeId)).size,
      waivedEmployees: new Set(open.filter((e) => e.isWaived).map((e) => e.employeeId)).size,
      estimatedEmployeeContributionPerPayroll: Math.round(employeeTotal * 100) / 100,
      estimatedEmployerContributionPerPayroll: Math.round(employerTotal * 100) / 100,
      contributionNote: notes.join(' '),
      upcoming: upcoming.slice(0, 10),
      categoryBreakdown: BENEFIT_CATEGORY_CODES.map((code) => ({
        categoryCode: code,
        activePlans: activePlans.filter((p) => p.categoryCode === code).length,
        enrolled: enrolled.filter((e) => planOf(e.benefitCodeId)?.categoryCode === code).length,
      })),
      recentChanges: recentChanges(this.audit),
    };
  }

  // ---- elections ----

  async listElections(query: ElectionQuery = {}): Promise<Paged<BenefitElection>> {
    guardRead();
    const today = todayIso();
    const status = query.status ?? 'all';
    let rows = this.elections.filter((e) => (!query.planId || e.benefitCodeId === query.planId) && (!query.employeeId || e.employeeId === query.employeeId));
    if (status === 'active') rows = rows.filter((e) => inEffect(e, today));
    if (status === 'upcoming') rows = rows.filter((e) => e.effectiveStartDate > today);
    if (status === 'ended') rows = rows.filter((e) => e.effectiveEndDate !== null && e.effectiveEndDate <= today);
    rows = [...rows].sort((a, b) => b.effectiveStartDate.localeCompare(a.effectiveStartDate) || a.employeeId.localeCompare(b.employeeId));
    // The company-wide list carries the plan's name and category, so a table needs no second call.
    const page = paginate(rows, query.page, query.pageSize);
    return {
      ...page,
      items: page.items.map((e) => {
        const plan = this.plans.find((p) => p.id === e.benefitCodeId);
        return { ...e, planName: plan?.name ?? null, planCategoryCode: plan?.categoryCode ?? null };
      }),
    };
  }

  async listEmployeeElections(employeeId: string): Promise<BenefitElection[]> {
    guardRead();
    return this.elections.filter((e) => e.employeeId === employeeId).sort((a, b) => b.effectiveStartDate.localeCompare(a.effectiveStartDate));
  }

  async createElection(employeeId: string, input: CreateBenefitElectionInput): Promise<BenefitElection> {
    guardWrite();
    const plan = this.plans.find((p) => p.id === input.benefitCodeId);
    if (!plan) throw problem(404, BdErrorCode.NotFound, 'That benefit plan is not available.');
    const directory = await loadEmployeeDirectory();
    if (!directory.some((e) => e.id === employeeId)) throw problem(404, BdErrorCode.NotFound, 'That employee was not found.');

    const waived = input.isWaived === true;
    // The server's order: inactive; a waiver is then always allowed (it is not even checked against the plan's dates); the plan's dates; tax review; coverage level.
    if (!plan.isActive) throw problem(409, BdErrorCode.PlanInactive, 'This plan is inactive, so it cannot take new enrollments or waivers.');
    if (!waived) {
      if ((plan.effectiveStartDate && input.effectiveStartDate < plan.effectiveStartDate) || (plan.effectiveEndDate && input.effectiveStartDate > plan.effectiveEndDate)) {
        throw problem(409, BdErrorCode.PlanNotEffective, "The election start date is outside this plan's effective dates.");
      }
      if (plan.configurationStatus !== 'Complete') {
        throw problem(409, BdErrorCode.ConfigurationIncomplete, 'This plan is awaiting tax review, so employees cannot be enrolled in it yet. A waiver can still be recorded.');
      }
      if (plan.contributionMethod === 'TieredByCoverage' && (!input.coverageLevel || input.coverageLevel === 'Waived' || !plan.coverageTiers.some((t) => t.coverageLevel === input.coverageLevel))) {
        throw problem(409, BdErrorCode.CoverageLevelNotOffered, 'Choose a coverage level this plan offers.');
      }
      if ((input.amountOverride ?? 0) < 0 || (input.employeeContributionPercentOverride ?? 0) < 0 || (input.companyContributionPercentOverride ?? 0) < 0) {
        throw problem(400, BdErrorCode.ValidationFailed, 'Contribution amounts cannot be negative.');
      }
    }
    const reason = input.qualifyingEventReason?.trim() ? input.qualifyingEventReason.trim() : null;
    if (reason && reason.length > 200) throw problem(400, BdErrorCode.ValidationFailed, 'The reason must be 200 characters or fewer.');
    const endProblem = endDateRefusal(input.effectiveStartDate, input.effectiveEndDate);
    if (endProblem) throw problem(400, BdErrorCode.ValidationFailed, endProblem);
    // The server's duplicate rule: a second open election of the same plan is refused unless the caller says it replaces the current one.
    const overlap = resolveOverlap(this.elections.filter((e) => e.employeeId === employeeId && e.benefitCodeId === plan.id), input.effectiveStartDate, input.replaceCurrent === true);
    if (overlap.kind === 'refused') throw problem(409, BdErrorCode.DuplicateActiveAssignment, overlap.message);

    const now = new Date().toISOString();
    const created: BenefitElection = {
      id: newId(),
      employeeId,
      benefitCodeId: plan.id,
      employeeContributionPercentOverride: waived ? null : input.employeeContributionPercentOverride ?? null,
      companyContributionPercentOverride: waived ? null : input.companyContributionPercentOverride ?? null,
      amountOverride: waived ? null : input.amountOverride ?? null,
      effectiveStartDate: input.effectiveStartDate,
      effectiveEndDate: input.effectiveEndDate || null,
      coverageLevel: waived ? 'Waived' : input.coverageLevel ?? null,
      isWaived: waived,
      qualifyingEventReason: reason,
      enrollmentSource: input.enrollmentSource ?? 'AdminEnrolled',
      planName: null,
      planCategoryCode: null,
      concurrencyVersion: 1,
      createdAtUtc: now,
      updatedAtUtc: null,
    };
    // Replacing: the overlapping election ends on the new start date, and its version moves on.
    const truncated = new Set(overlap.kind === 'replace' ? overlap.truncate.map((e) => e.id) : []);
    this.elections = this.elections.map((e) =>
      truncated.has(e.id) ? { ...e, effectiveEndDate: input.effectiveStartDate, concurrencyVersion: e.concurrencyVersion + 1, updatedAtUtc: now } : e,
    );
    this.elections = [...this.elections, created];
    this.record('BenefitElectionCreated', `${waived ? 'A waiver was recorded' : 'An employee was enrolled'} for plan ${plan.name}.`);
    return created;
  }

  async endElection(id: string, effectiveEndDate: string, expectedVersion?: number): Promise<BenefitElection> {
    guardWrite();
    const existing = this.elections.find((e) => e.id === id);
    if (!existing) throw problem(404, BdErrorCode.NotFound, 'That election was not found.');
    if (expectedVersion !== undefined && expectedVersion !== existing.concurrencyVersion) throw problem(409, BdErrorCode.VersionConflict, 'This election was changed by someone else. Reload it and try again; nothing was changed.');
    if (effectiveEndDate <= existing.effectiveStartDate) {
      throw problem(400, BdErrorCode.ValidationFailed, 'The end date must be after the election\'s start date.');
    }
    const updated = { ...existing, effectiveEndDate, concurrencyVersion: existing.concurrencyVersion + 1, updatedAtUtc: new Date().toISOString() };
    this.elections = this.elections.map((e) => (e.id === id ? updated : e));
    const plan = this.plans.find((p) => p.id === existing.benefitCodeId);
    this.record('BenefitElectionEnded', `An election for plan ${plan?.name ?? 'a plan'} was ended.`);
    return updated;
  }
}
